import * as React from 'react'

import * as SwitchPrimitives from '@radix-ui/react-switch'

import { cn } from '@/shared/utils/helpers'

export interface SwitchProps
	extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
	/** Label text to display next to the switch */
	label?: string
	/** Description text to display below the label */
	description?: string
}

const Switch = React.forwardRef<
	React.ComponentRef<typeof SwitchPrimitives.Root>,
	SwitchProps
>(({ className, label, description, ...props }, ref) => {
	const switchElement = (
		<SwitchPrimitives.Root
			className={cn(
				'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-border shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-main-accent data-[state=unchecked]:bg-muted',
				className
			)}
			{...props}
			ref={ref}
		>
			<SwitchPrimitives.Thumb
				className={cn(
					'pointer-events-none block size-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0'
				)}
			/>
		</SwitchPrimitives.Root>
	)

	if (label || description) {
		return (
			<div className="flex items-start gap-3">
				{switchElement}
				<div className="flex flex-col gap-1">
					{label && (
						<label
							className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
							onClick={() => {
								if (!props.disabled) {
									props.onCheckedChange?.(!props.checked)
								}
							}}
						>
							{label}
						</label>
					)}
					{description && (
						<p className="text-sm text-muted-foreground">{description}</p>
					)}
				</div>
			</div>
		)
	}

	return switchElement
})
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
